import moment from "moment";

import { AdminBillableServiceGridSortColumn } from "@/Administration/Models/UI/BillableService/AdminBillableServiceGridSortColumn";
import { BillableServiceUIModel } from "@/Administration/Models/UI/BillableService/BillableServiceUIModel";
import { IBillableServiceModel } from "@/Administration/Models/UI/BillableService/IBillableServiceModel";
import { PrivateBillCostCenter } from "@/Common/Components/Billing/PrivateBillCostCenter";
import { ButtonControl } from "@/Common/Components/Buttons/ButtonControl";
import { SpanControl } from "@/Common/Components/SpanControl";
import { CommonTableHeaderComponent } from "@/Common/Components/Tables/CommonTableHeaderComponent";
import { MyleTable } from "@/Common/Components/Tables/MyleTable";
import { MyleTableColumn, MyleTableColumnOptions } from "@/Common/Components/Tables/MyleTableColumn";
import { MyleTableColumnOrderControlv2 } from "@/Common/Components/Tables/MyleTableColumnOrderControlv2";
import { SimplePagerControl } from "@/Common/Components/Tables/SimplePagerControl";
import { MyleHelper } from "@/Common/Helpers/MyleHelper";
import { SortDirection } from "@/Common/Models/UI/Enums";
import { ColumnConfigurationModel } from "@/Common/Models/UI/Tables/ColumnConfigurationModel";
import { ITableRenderOptions } from "@/Common/Models/UI/Tables/ITableRenderOptions";
import { MyleTableTarget } from "@/Common/Models/UI/Tables/MyleTableTarget";
import * as LocalOperations from "@/Common/Operations/operations.local";
import { BillableServiceSearchSettings } from "./BillableServiceSearchSettings";
import { EditBillableServiceComponent } from "./EditBillableServiceComponent";

interface IBillableServiceTabViewProps {
    model: IBillableServiceModel;
    costCenters: PrivateBillCostCenter[];
}

class BillableServiceTabView extends React.Component<IBillableServiceTabViewProps, never> {
    private helper: MyleHelper = new MyleHelper();
    private saveOptionKey: string = "AdminBillableServiceGrid";

    public componentDidMount(): void {
        sessionStore.dispatchAsync(LocalOperations.AdministrationBillableService.RefreshGrid, { reload: true });
        window.setTimeout(() => {
            $(window).trigger("resize");
        }, 50);
    }

    public render(): JSX.Element {
        const columnsOptions = this.helper.loadTableColumnOptionsFromSettings(this.saveOptionKey, this.getMyleTableColumnsDefaultOptions());
        const billableServices = this.props.model.get("FilteredBillableServices").toArray();
        const tableRenderOptions = this.loadTableRenderOptions(columnsOptions);
        const cfgm: ColumnConfigurationModel[] = this.helper.castColumnConfigurationToMyleTableColumnOptions(columnsOptions.toArray());

        const tableColumns = this.getTableColumns(columnsOptions);

        return (
            <div className="workspace">
                <CommonTableHeaderComponent
                    title={cacheStore.GetString("BillableServicesLabel")}
                    searchText={this.props.model.get("BillableServiceSearchText")}
                    searchPlaceholder={cacheStore.GetString("SearchField")}
                    onSearch={text => sessionStore.dispatch(LocalOperations.AdministrationBillableService.SearchSettings_SearchTextChanged, { filterText: text })}
                    onRefreshClick={() => sessionStore.dispatch(LocalOperations.AdministrationBillableService.RefreshGrid, { reload: true })}
                    onSettingsClick={() => sessionStore.dispatch(LocalOperations.AdministrationBillableService.SearchSettings_Open)}
                />

                <div id="posref" className="ws-scrollable">

                    {this.props.model.get("SearchMenuOpen") && (
                        <BillableServiceSearchSettings
                            id="billableServiceSettings"
                            model={this.props.model}
                            className="filter-component small filter-component-responsive"
                            refreshServiceCallBack={null}
                            onOkCallBack={null}
                            onCancelCallBack={() => this.onCloseSearchSettings()}
                        />
                    )}

                    {this.props.model.get("DisplayEditBillableServiceComponent") && (
                        <EditBillableServiceComponent
                            currentEditMode={this.props.model.get("CurrentEditMode")}
                            editedBillableService={this.props.model.get("EditedBillableService")}
                            onCloseOperation={LocalOperations.AdministrationBillableService.BillableServiceEdit_Close}
                        />
                    )}

                    <div>
                        <MyleTable
                            TableRenderOptions={tableRenderOptions}
                            TableId="adminBillableServices_table"
                            Columns={tableColumns}
                            DataSource={billableServices.toImmutable()}
                            RowClickOperation={LocalOperations.AdministrationBillableService.BillableServiceEdit_Open}
                            SaveOptionskeyTarget={this.saveOptionKey}
                            ColumnReorderTarget={MyleTableTarget.Unassigned}
                            ColumnSortTarget={MyleTableTarget.Myle_AdminBillableServices_ColumnSortClick}
                        />
                    </div>
                    <div className="clearfix" />

                </div>

                <div className="ws-footer table-pager-footer">
                    <div className="row">
                        <div className="col-lg-4 col-md-4 col-sm-4">
                            <SimplePagerControl
                                PageSize={this.props.model.get("SelectedPageSizeOption")}
                                TotalRows={this.props.model.get("TotalQueryRows")}
                                CurrentPage={this.props.model.get("CurrentPage")}
                                EventToDispatchOnPageChange={LocalOperations.AdministrationBillableService.BillableService_CurrentPageChange}
                            />
                        </div>
                        <div className="col-lg-8 col-md-8 col-sm-8 flex-display flex-end">
                            <ButtonControl
                                variant="brand"
                                onClick={() => sessionStore.dispatch(LocalOperations.AdministrationBillableService.OpenCreateNewBillableService)}
                                label={cacheStore.GetString("NewServiceLabel")}
                            />
                        </div>
                    </div>
                </div>

                {this.props.model.get("ColumnOrderingPopupOpen") && (
                    <MyleTableColumnOrderControlv2
                        TableColumns={cfgm}
                        SaveOptionskeyTarget={this.saveOptionKey}
                        ContainerId="columnSettingsPopup"
                        CloseColumOrder={LocalOperations.AdministrationBillableService.SearchSettings_ColumnOrderingPopup_Open}
                    />
                )}

            </div>
        );
    }

    private getMyleTableColumnsDefaultOptions(): MyleTableColumnOptions[] {
        return [
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("SiteDLabel"),
                FieldName: "Site",
                // IsFixedInPlace: true,
                RowClickEnabled: true,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("DepartmentBillableServiceLabel"),
                FieldName: "CostCenter",
                RowClickEnabled: true,
                WidthPercentageDefault: 15
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NumberPhoneLabel"),
                FieldName: "Number",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: AdminBillableServiceGridSortColumn.Number,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 10
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("NameLabel"),
                FieldName: "Name",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: AdminBillableServiceGridSortColumn.Name,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 30
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("UnitPriceAdminLabel"),
                FieldName: "Price",
                RowClickEnabled: true,
                IsSortable: true,
                SortColumn: AdminBillableServiceGridSortColumn.Price,
                SortDirection: SortDirection.ASC,
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("TaxesLabel"),
                FieldName: "Taxes",
                RowClickEnabled: true,
                WidthPercentageDefault: 5
            }),
            new MyleTableColumnOptions({
                HeaderText: cacheStore.GetString("LastUpdateLabel"),
                FieldName: "LastUpdate",
                RowClickEnabled: false,
                WidthPercentageDefault: 15
            })
        ];
    }

    private loadTableRenderOptions(columnOptions: Immutable.List<MyleTableColumnOptions>): ITableRenderOptions {
        let defaultTablePluginOptions = {
            MinColWidth: 60,
            MinTableWidth: 980,
            Cols: columnOptions.filter(x => x.IsHidden === false).map(i => i.WidthPercentage),
            FirstColumnNoDrag: false,
            LastColumnNoDrag: false,
            Autoresize: true,
            OnColumnWidthChange: null
        };

        return Immutable.fromJS(defaultTablePluginOptions);
    }

    private getTableColumns(columnsOptions: Immutable.List<MyleTableColumnOptions>): Immutable.List<MyleTableColumn> {
        return MyleTableColumn.buildList<BillableServiceUIModel>(
            columnsOptions,
            column => {
                switch (column.FieldName) {
                    case "Site":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={(item.SiteId && item.SiteId.length > 0) ? cacheStore.GetSiteNameById(item.SiteId) : "--"} /> </div>
                            )
                        };

                    case "CostCenter":
                        return {
                            renderCell: item => (
                                <div>
                                    <SpanControl
                                        text={(item.Model.CostCenterId && item.Model.CostCenterId.length > 0)
                                            ? PrivateBillCostCenter.GetDisplayName(cacheStore.GetCostCenterById(item.Model.CostCenterId))
                                            : ""
                                        }
                                    />
                                </div>
                            )
                        };

                    case "Number":
                        return {
                            renderCell: item => (
                                <div><SpanControl text={item.Number.toString()} /> </div>
                            )
                        };

                    case "Name":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Name} /> </div>
                            )
                        };

                    case "Price":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Price != null ? `$ ${item.Price}` : "--"} /> </div>
                            )
                        };

                    case "Taxes":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.getStringTaxesForTable()} /> </div>
                            )
                        };

                    case "LastUpdate":
                        return {
                            renderCell: item => (
                                <div> <SpanControl text={item.Model.LastUpdateDate ? moment(item.Model.LastUpdateDate).format("YYYY-MM-DD  hh:mm:ss") : ""} /> </div>
                            )
                        };
                }
            },
            false
        );
    }

    private onCloseSearchSettings(): void {
        sessionStore.dispatch(LocalOperations.AdministrationBillableService.SearchSettings_Close);
    }
}

export { BillableServiceTabView };
